/**
 * Shared email layouts: details, the author's unsaved edits (name + HTML), and
 * every lifecycle command. Same rules as templates: in-memory edits only, every
 * command names the lineage revision it was based on, one idempotency key per
 * intent, conflicts keep the edits and reload. Guards (in use / default /
 * archived) are the server's: refusals are shown with the counts it returns.
 */
import { useCallback, useRef, useState } from "react";
import type {
  MessageLayoutDetail,
  MessageLayoutPublishImpact,
  MessageBuilderWriteWarning,
  MessagingDefaultLayout,
} from "@v0y4ge/api-contracts";
import { builderApi, classifyFailure, type Failure } from "./api";
import { outcomeUnknown, runIntent, type CommandResult } from "./commands";

export interface LayoutWork { name: string; html: string }
export type LayoutDetailState =
  | { phase: "loading" }
  | { phase: "error"; failure: Failure }
  | { phase: "ready"; detail: MessageLayoutDetail; refreshing: boolean };
export type LayoutBusy = "save" | "publish" | "discard" | "archive" | "restore" | "delete" | "default" | "create" | null;

const baseOf = (d: MessageLayoutDetail): LayoutWork => ({ name: d.name, html: d.html });
/** The lineage guard every layout mutation carries, read from the server's row. */
const guardOf = (d: MessageLayoutDetail) => ({ expectedRevision: d.revision, expectedGeneration: d.generation });
const same = (a: LayoutWork | undefined, b: LayoutWork | undefined) => !!a && !!b && a.name === b.name && a.html === b.html;
const noDetail: Failure = { kind: "failed", code: "", status: 0, message: "", fields: [], params: {} };

export interface LayoutStore {
  detailOf(key: string): LayoutDetailState | undefined;
  load(key: string, force?: boolean): void;
  workOf(key: string): LayoutWork | null;
  setWork(key: string, next: LayoutWork): void;
  dirty(key: string): boolean;
  busy(key: string): LayoutBusy;
  warnings(key: string): MessageBuilderWriteWarning[];
  discardLocal(key: string): void;
  save(key: string, note?: string): Promise<CommandResult<MessageLayoutDetail>>;
  publish(key: string, note?: string): Promise<CommandResult<{ detail: MessageLayoutDetail; impact: MessageLayoutPublishImpact; savedFirst: boolean }>>;
  discardDraft(key: string): Promise<CommandResult<MessageLayoutDetail>>;
  archive(key: string): Promise<CommandResult<MessageLayoutDetail>>;
  restore(key: string): Promise<CommandResult<MessageLayoutDetail>>;
  remove(key: string): Promise<CommandResult<true>>;
  create(name: string, from: { kind: "blank" } | { kind: "copy"; layoutKey: string; version: "published" | "latest" }): Promise<CommandResult<MessageLayoutDetail>>;
  setDefault(layoutKey: string, current: MessagingDefaultLayout): Promise<CommandResult<{ changed: boolean; inheriting: number }>>;
  anyDirty(): boolean;
}

export function useLayoutStore(onChanged: () => void): LayoutStore {
  const details = useRef(new Map<string, LayoutDetailState>());
  const works = useRef(new Map<string, LayoutWork>());
  const busyMap = useRef(new Map<string, LayoutBusy>());
  const warn = useRef(new Map<string, MessageBuilderWriteWarning[]>());
  const loads = useRef(new Map<string, number>());
  /** Per layout, the edits a discard-draft request was asked over (undefined: none) and the idempotency key it was sent with. */
  const discards = useRef(new Map<string, { k: string; work: LayoutWork | undefined }>());
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((n) => n + 1), []);

  const put = useCallback((key: string, detail: MessageLayoutDetail) => {
    details.current.set(key, { phase: "ready", detail, refreshing: false });
    const w = works.current.get(key);
    if (w && same(w, baseOf(detail))) works.current.delete(key);
    bump();
  }, [bump]);

  const load = useCallback((key: string, force = false) => {
    const cur = details.current.get(key);
    if (cur && !force && cur.phase !== "error") return;
    const mine = (loads.current.get(key) || 0) + 1;
    loads.current.set(key, mine);
    details.current.set(key, cur && cur.phase === "ready" ? { ...cur, refreshing: true } : { phase: "loading" });
    bump();
    builderApi.getMessageLayout(key).then(
      (res) => { if (loads.current.get(key) === mine) put(key, res.data); },
      (error: unknown) => {
        if (loads.current.get(key) !== mine) return;
        const prev = details.current.get(key);
        details.current.set(key, prev && prev.phase === "ready" ? { ...prev, refreshing: false } : { phase: "error", failure: classifyFailure(error) });
        bump();
      },
    );
  }, [bump, put]);

  const ready = (key: string) => { const d = details.current.get(key); return d && d.phase === "ready" ? d.detail : null; };
  const withBusy = async <T,>(key: string, kind: LayoutBusy, fn: () => Promise<T>): Promise<T> => {
    busyMap.current.set(key, kind); bump();
    try { return await fn(); } finally { busyMap.current.delete(key); bump(); }
  };
  const after = <T,>(key: string, r: CommandResult<T>): CommandResult<T> => {
    if (!r.ok && r.failure.kind === "conflict") load(key, true);
    if (r.ok) onChanged();
    return r;
  };

  const saveNow = async (key: string, note?: string): Promise<CommandResult<MessageLayoutDetail>> => {
    const d = ready(key); const w = works.current.get(key);
    if (!d || !w) return { ok: false, failure: noDetail };
    const body = { ...guardOf(d), html: w.html, ...(w.name !== d.name ? { name: w.name } : {}), ...(note ? { note } : {}) };
    const r = await runIntent(`layout:${key}:save`, body, (k) => builderApi.saveMessageLayoutDraft(key, body, { idempotencyKey: k }));
    if (!r.ok) return r;
    warn.current.set(key, r.value.warnings || []);
    const latest = works.current.get(key);
    details.current.set(key, { phase: "ready", detail: r.value.data, refreshing: false });
    if (!latest || same(latest, w) || same(latest, baseOf(r.value.data))) works.current.delete(key);
    bump();
    return { ok: true, value: r.value.data };
  };

  const command = (key: string, kind: Exclude<LayoutBusy, null>, call: (body: { expectedRevision: number; expectedGeneration: string }, k: string) => Promise<{ data: MessageLayoutDetail; warnings?: MessageBuilderWriteWarning[] }>) =>
    withBusy(key, kind, async (): Promise<CommandResult<MessageLayoutDetail>> => {
      const d = ready(key); if (!d) return { ok: false, failure: noDetail };
      const body = guardOf(d);
      const r = await runIntent(`layout:${key}:${kind}`, body, (k) => call(body, k));
      if (r.ok) { warn.current.set(key, r.value.warnings || []); put(key, r.value.data); return after(key, { ok: true, value: r.value.data }); }
      return after(key, r);
    });

  return {
    detailOf: (key) => details.current.get(key),
    load,
    workOf: (key) => works.current.get(key) || (ready(key) ? baseOf(ready(key) as MessageLayoutDetail) : null),
    setWork: (key, next) => { const d = ready(key); if (d && same(next, baseOf(d))) works.current.delete(key); else works.current.set(key, next); warn.current.delete(key); bump(); },
    dirty: (key) => works.current.has(key),
    busy: (key) => busyMap.current.get(key) ?? null,
    warnings: (key) => warn.current.get(key) || [],
    discardLocal: (key) => { works.current.delete(key); warn.current.delete(key); bump(); },
    save: (key, note) => withBusy(key, "save", async () => after(key, await saveNow(key, note))),
    publish: (key, note) => withBusy(key, "publish", async () => {
      let savedFirst = false;
      if (works.current.has(key)) { const s = await saveNow(key, note); if (!s.ok) return after(key, s); savedFirst = true; }
      const d = ready(key); if (!d) return { ok: false as const, failure: noDetail };
      const body = { ...guardOf(d), ...(note ? { note } : {}) };
      const r = await runIntent(`layout:${key}:publish`, body, (k) => builderApi.publishMessageLayout(key, body, { idempotencyKey: k }));
      if (!r.ok) return after(key, r);
      warn.current.set(key, r.value.warnings || []);
      put(key, r.value.data);
      return after(key, { ok: true as const, value: { detail: r.value.data, impact: r.value.impact, savedFirst } });
    }),
    // Nothing local is cleared until the server confirms the discard: while it is pending, refused, conflicted or
    // unanswered, the edits, the dirty state and the warnings stay. On success only the edits present when the
    // author asked are cleared; newer edits survive. That record is bound to the idempotency key k: a replay under
    // the same k (unknown outcome) reuses it, a new k (new intent, e.g. after a guard refresh) captures it afresh.
    discardDraft: (key) => command(key, "discard", (body, k) => {
      const held = discards.current.get(key);
      const intended = held && held.k === k ? held.work : works.current.get(key);
      discards.current.set(key, { k, work: intended });
      return builderApi.discardMessageLayoutDraft(key, body, { idempotencyKey: k }).then(
        (res) => { if (discards.current.get(key)?.k === k) discards.current.delete(key); if (same(works.current.get(key), intended)) works.current.delete(key); return res; },
        (error: unknown) => { if (!outcomeUnknown(classifyFailure(error)) && discards.current.get(key)?.k === k) discards.current.delete(key); throw error; },
      );
    }),
    archive: (key) => command(key, "archive", (body, k) => builderApi.archiveMessageLayout(key, body, { idempotencyKey: k })),
    restore: (key) => command(key, "restore", (body, k) => builderApi.restoreMessageLayout(key, body, { idempotencyKey: k })),
    remove: (key) => withBusy(key, "delete", async (): Promise<CommandResult<true>> => {
      const d = ready(key); if (!d) return { ok: false, failure: noDetail };
      const guard = guardOf(d);
      const r = await runIntent(`layout:${key}:delete`, guard, (k) => builderApi.deleteMessageLayout(key, guard, { idempotencyKey: k }));
      if (!r.ok) return after(key, r);
      details.current.delete(key); works.current.delete(key); bump();
      return after(key, { ok: true, value: true });
    }),
    create: (name, from) => withBusy("__new__", "create", async (): Promise<CommandResult<MessageLayoutDetail>> => {
      const body = { name, from };
      const r = await runIntent("layout:create", body, (k) => builderApi.createMessageLayout(body, { idempotencyKey: k }));
      if (!r.ok) return r;
      put(r.value.data.layoutKey, r.value.data);
      onChanged();
      return { ok: true, value: r.value.data };
    }),
    setDefault: (layoutKey, current) => withBusy(layoutKey, "default", async () => {
      const body = { layoutKey, expectedRevision: current.revision };
      const r = await runIntent("layout:default", body, (k) => builderApi.setMessagingDefaultLayout(body, { idempotencyKey: k }));
      if (!r.ok) { if (r.failure.kind === "conflict") onChanged(); return r; }
      onChanged();
      return { ok: true as const, value: { changed: r.value.data.changed, inheriting: r.value.data.inheriting } };
    }),
    anyDirty: () => works.current.size > 0,
  };
}
